import React, {useEffect, useState, useMemo, useCallback} from "react";
import {
    Button,
    Card,
    CardContent,
    Grid,
    InputAdornment,
    OutlinedInput,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    CircularProgress,
    IconButton
} from "@mui/material";
import {MappingArrow, SearcherIcon} from "../../../../../../utils/icons";
import { tableRowSx, inputHeight, pageContentGap } from "../../../../../../theme";
import {getAllCategories, AI_MAPPING_ENABLED} from "../../../../../../utils/utils";
import {MappingHeader, MappingCell} from "../advanced_supplier_import";
import {SectionTitle} from "../../../export/advanced_export";
import EnhancedSelect from "../../../../../../components/EnhancedSelect";
import NavigationButtons from "./NavigationButtons";
import ErrorIcon from "@mui/icons-material/Error";
import { Clear } from "@mui/icons-material";
import cloneDeep from 'lodash/cloneDeep';
import Fuse from 'fuse.js';
import MappingReviewDialog from "../../../../components/Dialogs/MappingReviewDialog";
import {aiMapAttributes} from "../../../../services/ai_service";
import EnhancedSnackbar from "../../../../../../components/EnhancedSnackbar";
import type { WizardSharedProps } from "../types";

// region TypeScript Interfaces
type CategoryMappingProps = WizardSharedProps;

interface CategoryEntry {
    Name: string;
    Path: string;
    Id?: unknown;
    [key: string]: unknown;
}

interface SupplierCategoryEntry {
    label: string;
    value: string;
}

interface MappingSuggestion {
    supplierAttribute: string;
    retailerAttribute: string;
    confidence: number;
    source: string;
}

interface SnackbarState {
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
}

interface LazyRetailerSelectProps {
    category: SupplierCategoryEntry;
    loading: boolean;
    onChange: (e: any, category: SupplierCategoryEntry) => void;
    mappedCategories: Map<string, string | null>;
    retailerLookupByName: Map<string, CategoryEntry>;
    retailerCategories: CategoryEntry[];
}

interface CategoryRowProps extends LazyRetailerSelectProps {
    onCategoryChange: (e: any, category: SupplierCategoryEntry) => void;
}
// endregion

const LazyRetailerSelect = React.memo(function LazyRetailerSelect({
                                                                     category,
                                                                     loading,
                                                                     onChange,
                                                                     mappedCategories,
                                                                     retailerLookupByName,
                                                                     retailerCategories
                                                                 }: LazyRetailerSelectProps) {
    const [options, setOptions] = useState<Array<{label: string; value: string; id: unknown}>>([]);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');

    useEffect(() => {
        if (!retailerCategories || retailerCategories.length === 0) {
            setOptions([]);
            return;
        }

        if (!isOpen && !searchValue) {
            setOptions([]);
            return;
        }

        if (!searchValue) {
            const MAX_INITIAL = 100;
            const limited = retailerCategories.slice(0, MAX_INITIAL).map(cat => ({
                label: cat.Path,
                value: cat.Name,
                id: cat.Id
            }));
            setOptions(limited);

            return;
        }

        const searchLower = searchValue.toLowerCase();
        const filtered = retailerCategories
            .filter(cat =>
                cat.Name.toLowerCase().includes(searchLower) ||
                cat.Path.toLowerCase().includes(searchLower)
            )
            .slice(0, 200)
            .map(cat => ({
                label: cat.Path,
                value: cat.Name,
                id: cat.Id
            }));

        setOptions(filtered);
    }, [searchValue, retailerCategories, isOpen]);

    const currentMapping = mappedCategories.get(category.label);
    const currentRetailer = currentMapping ? retailerLookupByName.get(currentMapping) : null;

    const value = useMemo(() =>
            currentMapping ? {
                label: currentRetailer?.Path ?? "",
                value: currentMapping
            } : null
        , [currentMapping, currentRetailer]);

    return (
        <EnhancedSelect
            isClearable
            isLoading={loading}
            placeholder={retailerCategories.length > 100 ? "Type to search categories..." : "Retailer Category"}
            options={options}
            value={value}
            onMenuOpen={() => setIsOpen(true)}
            onMenuClose={() => {
                setIsOpen(false);
                setSearchValue('');
            }}
            onInputChange={(value: string, { action }: { action: string }) => {
                if (action === 'input-change') {
                    setSearchValue(value);
                }
            }}
            searchableFields={['label', 'value', 'id']}
            formatOptionLabel={(option: any) => (
                <Grid container style={{gap: "4px", flexDirection: "column"}}>
                    <Typography style={{fontWeight: "bold"}}>
                        {option.value}
                    </Typography>

                    <Typography style={{fontSize: "0.8rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%"}}>
                        {option.label}
                    </Typography>

                    <Typography style={{fontSize: "0.8rem"}}>
                        {option.id}
                    </Typography>
                </Grid>
            )}
            onChange={(e: any) => onChange(e, category)}
            itemHeight={100}
            noOptionsMessage={() =>
                searchValue ? "No matching categories found" : "Type to search categories"
            }
        />
    );
});


const searchSx = {
    height: inputHeight,
    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "6px" }
};

const CategoryRow = React.memo(function CategoryRow({
                                                        category,
                                                        loading,
                                                        onCategoryChange,
                                                        mappedCategories,
                                                        retailerLookupByName,
                                                        retailerCategories
                                                    }: CategoryRowProps) {

    return (
        <TableRow key={category.label} sx={{...tableRowSx, padding: "0"}}>
            <MappingCell style={{padding: "0"}}>
                <Grid container justifyContent={"space-between"} alignItems={"center"}
                      style={{display: "flex", gap: pageContentGap, marginRight: "12px"}}>
                    <EnhancedSelect
                        dropdownIndicatorTemplate={<></>}
                        containerStyle={{pointerEvents: "none"}}
                        placeholder={"Your Category"}
                        value={{
                            label: category.label,
                            value: category.label,
                        }}
                    />
                    <MappingArrow/>
                </Grid>
            </MappingCell>
            <MappingCell style={{padding: "0"}}>
                <LazyRetailerSelect
                    category={category}
                    loading={loading}
                    onChange={onCategoryChange}
                    mappedCategories={mappedCategories}
                    retailerLookupByName={retailerLookupByName}
                    retailerCategories={retailerCategories}
                />
            </MappingCell>
        </TableRow>
    );
}, (prevProps, nextProps) => {
    const prevMapping = prevProps.mappedCategories.get(prevProps.category.label);
    const nextMapping = nextProps.mappedCategories.get(nextProps.category.label);
    return prevMapping === nextMapping &&
        prevProps.loading === nextProps.loading &&
        prevProps.category.label === nextProps.category.label &&
        prevProps.retailerCategories === nextProps.retailerCategories;
});

export default function CategoryMapping(props: CategoryMappingProps) {
    const {allowBack, allowNext, backStep, nextStep, activeStep, steps, setSteps} = props;
    const {profile, formattedProfile, profileType} = props;
    const {parsedFieldsWithData} = props;
    const {columnDataLoading} = props;
    const {setError} = props;

    const [loading, setLoading] = React.useState<boolean>(false);
    const [retailerCategories, setRetailerCategories] = React.useState<CategoryEntry[]>([]);
    const [supplierCategories, setSupplierCategories] = React.useState<SupplierCategoryEntry[]>([]);
    const [localMappings, setLocalMappings] = useState<Record<string, string | null | undefined>>(props.profile.CategoryMappings || {});

    const [aiMappingInProgress, setAiMappingInProgress] = useState<boolean>(false);
    const [mappingReviewDialogOpen, setMappingReviewDialogOpen] = useState<boolean>(false);
    const [mappingSuggestions, setMappingSuggestions] = useState<MappingSuggestion[]>([]);
    const [processingStep, setProcessingStep] = useState<number | null>(null);
    const [applyingMappings, setApplyingMappings] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [categoryFilter, setCategoryFilter] = useState<string>('');

    const getRetailerCategories = async () => {
        if(!profile.Partner && !props.isRetailer) {
            return;
        }

        let allRetailerCategories: any = await (getAllCategories as any)(profile.Partner, props.isRetailer);
        if (allRetailerCategories.errorMessage) {
            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: allRetailerCategories.errorMessage
            });
            return;
        }

        const MAX_CATEGORIES = 50000;
        if (allRetailerCategories.length > MAX_CATEGORIES) {
            console.warn(`Category count (${allRetailerCategories.length}) exceeds maximum (${MAX_CATEGORIES}). Truncating list.`);

            setSnackbar({
                open: true,
                message: `Found ${allRetailerCategories.length.toLocaleString()} categories. Limited to ${MAX_CATEGORIES.toLocaleString()} for performance. Use search to find specific categories.`,
                severity: 'warning'
            });

            const mappedCategoryNames = new Set(Object.values(profile.CategoryMappings || {}).filter(v => v));
            const prioritized: any[] = [];
            const others: any[] = [];

            allRetailerCategories.forEach((cat: any) => {
                if (mappedCategoryNames.has(cat.Name)) {
                    prioritized.push(cat);
                } else {
                    others.push(cat);
                }
            });

            allRetailerCategories = [
                ...prioritized,
                ...others.slice(0, MAX_CATEGORIES - prioritized.length)
            ];
        }

        const MAX_CATEGORIES_FOR_AI = 5000;
        if (AI_MAPPING_ENABLED() && allRetailerCategories.length > MAX_CATEGORIES_FOR_AI) {
            setSnackbar({
                open: true,
                message: `AI matching disabled for catalogs with more than ${MAX_CATEGORIES_FOR_AI.toLocaleString()} categories. Fuzzy matching is still available.`,
                severity: 'info'
            });
        }

        if (allRetailerCategories.length > 1000) {
            setRetailerCategories(allRetailerCategories);

            if ((window as any).requestIdleCallback) {
                (window as any).requestIdleCallback(() => {
                    allRetailerCategories.sort((a: CategoryEntry, b: CategoryEntry) =>
                        a.Path.localeCompare(b.Path)
                    );
                    setRetailerCategories([...allRetailerCategories]);
                });
            } else {
                setTimeout(() => {
                    allRetailerCategories.sort((a: CategoryEntry, b: CategoryEntry) =>
                        a.Path.localeCompare(b.Path)
                    );
                    setRetailerCategories([...allRetailerCategories]);
                }, 0);
            }
        } else {
            allRetailerCategories.sort((a: CategoryEntry, b: CategoryEntry) =>
                a.Path.localeCompare(b.Path)
            );
            setRetailerCategories(allRetailerCategories);
        }
    }

    const mapCategory = useCallback((e: any, category: SupplierCategoryEntry) => {
        if (e == null) {
            setLocalMappings(prev => {
                const next = { ...prev };
                next[category.label] = "";
                return next;
            });
            return;
        }

        setLocalMappings(prev => {
            const next = { ...prev };
            next[category.label] = e.value;
            return next;
        });
    }, []);

    const mappedCategories = useMemo(() => {
        const lookup = new Map<string, string | null>();
        supplierCategories.forEach(category => {
            lookup.set(category.label, localMappings[category.label] || null);
        });
        return lookup;
    }, [supplierCategories, localMappings]);

    const autoMatch = async () => {
        setAiMappingInProgress(true);
        setMappingReviewDialogOpen(true);
        setProcessingStep(0);

        const suggestions: MappingSuggestion[] = [];
        const unmatchedCategories: string[] = [];

        // Phase 1: Exact match (case-insensitive)
        const retailerLookup = new Map(
            retailerCategories.map(category => [
                category.Name.toLowerCase(),
                category.Name
            ])
        );

        supplierCategories.forEach(category => {
            if (localMappings[category.label]) return;

            const supplierName = category.label.toLowerCase();
            const exactMatch = retailerLookup.get(supplierName);

            if (exactMatch) {
                suggestions.push({
                    supplierAttribute: category.label,
                    retailerAttribute: exactMatch,
                    confidence: 1.0,
                    source: 'Fuzzy'
                });
            } else {
                unmatchedCategories.push(category.label);
            }
        });

        // Phase 2: Fuzzy matching for unmapped
        if (unmatchedCategories.length > 0) {
            const fuseOpts = {
                includeScore: true,
                threshold: 0.3,
                keys: ['Name']
            };
            const fuse = new Fuse(retailerCategories, fuseOpts);

            const stillUnmatched: string[] = [];
            unmatchedCategories.forEach(categoryName => {
                const result: any = fuse.search(categoryName);
                if (result.length > 0 && result[0].score < 0.3) {
                    suggestions.push({
                        supplierAttribute: categoryName,
                        retailerAttribute: result[0].item.Name,
                        confidence: 1 - result[0].score,
                        source: 'Fuzzy'
                    });
                } else {
                    stillUnmatched.push(categoryName);
                }
            });

            setProcessingStep(1);

            // Phase 3: AI mapping for still unmapped (if enabled)
            const MAX_CATEGORIES_FOR_AI = 5000;

            if (AI_MAPPING_ENABLED() && stillUnmatched.length > 0) {
                if (retailerCategories.length > MAX_CATEGORIES_FOR_AI) {
                    setSnackbar({
                        open: true,
                        message: `AI matching disabled for large catalog (${retailerCategories.length.toLocaleString()} categories). Using fuzzy matching only.`,
                        severity: 'info'
                    });
                } else {
                    try {
                        const aiResponse: any = await aiMapAttributes(
                            stillUnmatched,
                            retailerCategories.map(cat => cat.Name)
                        );

                        if (aiResponse.mappings) {
                            Object.entries(aiResponse.mappings as Record<string, any>).forEach(([supplierCat, mapping]) => {
                                suggestions.push({
                                    supplierAttribute: supplierCat,
                                    retailerAttribute: mapping.suggested,
                                    confidence: mapping.confidence,
                                    source: mapping.source
                                });
                            });
                        }
                    } catch (error) {
                        console.error('AI category mapping failed:', error);
                    }
                }
            }
        }

        setProcessingStep(2);
        setMappingSuggestions(suggestions);
        setAiMappingInProgress(false);
    };

    const handleApplyMappings = useCallback((acceptedMappings: MappingSuggestion[]) => {
        setApplyingMappings(true);

        const allNewMappings: Record<string, string> = {};

        acceptedMappings.forEach(mapping => {
            allNewMappings[mapping.supplierAttribute] = mapping.retailerAttribute;
        });

        const applyMappings = () => {
            setLocalMappings(prev => ({ ...prev, ...allNewMappings }));

            // Clean up
            setApplyingMappings(false);
            setMappingReviewDialogOpen(false);
            setMappingSuggestions([]);

            // Show success
            const mappingCount = acceptedMappings.length;
            setSnackbar({
                open: true,
                message: `Successfully applied ${mappingCount} category mapping${mappingCount !== 1 ? 's' : ''}`,
                severity: 'success'
            });
        };

        // For very large datasets, use requestIdleCallback for better performance
        if (acceptedMappings.length > 100 && (window as any).requestIdleCallback) {
            (window as any).requestIdleCallback(applyMappings, { timeout: 2000 });
        } else {
            // Small dataset, apply immediately
            applyMappings();
        }
    }, []);

    const onNext = () => {
        // Use Set for O(1) lookup performance with large datasets
        const mappedSet = new Set(Object.keys(localMappings));
        let unmappedCategories = supplierCategories.filter(category =>
            !mappedSet.has(category.label)
        );

        if (unmappedCategories.length > 0) {
            setSteps((prevState) => {
                let newState = cloneDeep(prevState);
                newState[activeStep].status = "error";
                newState[activeStep].errors = ["Not all categories are mapped!"];
                return newState;
            });
        } else {
            setSteps((prevState) => {
                let newState = cloneDeep(prevState);
                newState[activeStep].status = "success";
                newState[activeStep].errors = undefined;
                return newState;
            });
            // Only update profile when moving to next step
            props.setProfile(prev => ({
                ...prev,
                CategoryMappings: localMappings
            }));
            nextStep();
        }
    };

    const onBack = () => {
        props.setProfile(prev => ({
            ...prev,
            CategoryMappings: localMappings
        }));
        backStep();
    };

    useEffect(() => {
        if (profile.CategoryMappings) {
            const init = async () => {
                setLoading(true);
                await getRetailerCategories();
                setLoading(false);
            }
            init();
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.CategoryMappings]);

    // Own effect (deps parsedFieldsWithData) so rows populate when the deferred fetch resolves.
    useEffect(() => {
        const foundCategoryFields = parsedFieldsWithData.find((field: any) => field.field === props.profile.Category)?.values;
        if (foundCategoryFields) {
            setSupplierCategories(
                foundCategoryFields.map((row: string) => {
                    return {
                        label: row,
                        value: row
                    }
                })
            );
        } else {
            // Clear stale rows on column change; no-op when empty to avoid an extra render.
            setSupplierCategories((prev: any[]) => (prev.length ? [] : prev));
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsedFieldsWithData, props.profile.Category]);

    // Memory cleanup on unmount
    useEffect(() => {
        return () => {
            // Clear large arrays to free memory
            setRetailerCategories([]);
            setSupplierCategories([]);
            setMappingSuggestions([]);
            // Note: Don't clear localMappings as it may be needed by parent
        };
    }, []);

    const retailerLookupByName = useMemo(() => {
        const lookup = new Map<string, CategoryEntry>();
        retailerCategories.forEach(category => {
            lookup.set(category.Name, category);
        });
        return lookup;
    }, [retailerCategories]);

    const filteredSupplierCategories = useMemo(() => {
        if (!categoryFilter) return supplierCategories;

        const filterLower = categoryFilter.toLowerCase();
        return supplierCategories.filter(cat =>
            cat.label.toLowerCase().includes(filterLower)
        );
    }, [supplierCategories, categoryFilter]);

    const mappingStats = useMemo(() => {
        const mapped = Object.values(localMappings).filter(v => v).length;
        const total = supplierCategories.length;
        return {
            mapped,
            unmapped: total - mapped,
            percentage: total > 0 ? Math.round((mapped / total) * 100) : 0
        };
    }, [localMappings, supplierCategories]);

    const TableRows = React.useMemo(() => {
            const startIndex = page * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;

            return filteredSupplierCategories
                .slice(startIndex, endIndex)
                .map((category) => (
                    <CategoryRow
                        key={category.label}
                        category={category}
                        loading={loading}
                        onChange={mapCategory}
                        onCategoryChange={mapCategory}
                        mappedCategories={mappedCategories}
                        retailerLookupByName={retailerLookupByName}
                        retailerCategories={retailerCategories}
                    />
                ));
        }, [
            filteredSupplierCategories,
            page,
            rowsPerPage,
            loading,
            mapCategory,
            mappedCategories,
            retailerLookupByName,
            retailerCategories
        ]);

    return (
        <>
            <Grid container style={{gap: pageContentGap}}>
                {(steps[activeStep].status === "error" && steps[activeStep].errors) && (
                    <>
                        {steps[activeStep].errors.map((error, index) => {
                            return (
                                <Card id="cueMessage" style={{backgroundColor: '#ee3224', color: 'white', width: "100%"}}>
                                    <CardContent style={{display: 'flex', alignItems: 'center'}}>
                                        <ErrorIcon style={{marginRight: '1rem'}}/>
                                        <Typography style={{
                                            textAlign: "left",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            maxHeight: "100px"
                                        }}>
                                            {error}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </>
                )}

                <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                    <Grid container style={{gap: pageContentGap}}>
                        <Grid container justifyContent="space-between" alignItems="center" style={{flexDirection: "column", alignItems: "flex-start", width: "100%"}}>
                            <SectionTitle style={{marginBottom: "12px"}}>
                                Category Mapping
                            </SectionTitle>
                            <Typography variant="body2" color="textSecondary">
                                {mappingStats.mapped} of {supplierCategories.length} mapped ({mappingStats.percentage}%)
                            </Typography>
                        </Grid>

                        {columnDataLoading ? (
                            <Grid container direction="column" justifyContent="center" alignItems="center"
                                  style={{width: "100%", minHeight: "200px", gap: "12px"}}
                                  data-testid="column-data-loading">
                                <CircularProgress size={48} />
                                <Typography variant="h6" color="textSecondary">Loading column values…</Typography>
                            </Grid>
                        ) : (
                        <>
                        <Grid container style={{ marginBottom: '12px', width: "100%" }}>
                            <OutlinedInput
                                placeholder="Search your categories..."
                                value={categoryFilter}
                                style={{ width: "100%" }}
                                onChange={(e) => {
                                    setCategoryFilter(e.target.value);
                                    setPage(0);
                                }}
                                sx={searchSx}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearcherIcon/>
                                    </InputAdornment>
                                }
                                endAdornment={
                                    categoryFilter && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setCategoryFilter('');
                                                    setPage(0);
                                                }}
                                            >
                                                <Clear style={{width: "20px", height: "20px", opacity: "0.8"}}/>
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            />
                        </Grid>

                        <Grid container>
                            <Table style={{tableLayout: "fixed"}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{border: "none", padding: "0"}}>
                                            <Grid container alignItems={"center"} style={{gap: pageContentGap, marginRight: "12px"}}>
                                                <MappingHeader>
                                                    Your Category
                                                </MappingHeader>

                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    style={{
                                                        height: "32px",
                                                        lineHeight: 1
                                                    }}
                                                    onClick={() => autoMatch()}
                                                    disabled={loading || aiMappingInProgress}
                                                    startIcon={aiMappingInProgress ? <CircularProgress size={16} /> : null}
                                                >
                                                    {loading ? "Loading Categories..." : aiMappingInProgress ? "Matching..." : (AI_MAPPING_ENABLED() ? "Auto Match (AI)" : "Auto Match")}
                                                </Button>
                                            </Grid>
                                        </TableCell>

                                        <TableCell style={{border: "none", padding: "0"}}>
                                            <Grid container alignItems={"center"} style={{gap: pageContentGap}}>
                                                <MappingHeader>
                                                    Retailer Category
                                                </MappingHeader>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody style={{padding: "0"}}>
                                    {TableRows}
                                </TableBody>
                            </Table>

                            {filteredSupplierCategories.length > 0 && (
                                <TablePagination
                                    rowsPerPageOptions={[20, 50, 100]}
                                    component="div"
                                    count={filteredSupplierCategories.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    style={{width: "100%"}}
                                />
                            )}

                            {filteredSupplierCategories.length === 0 && (
                                <Typography style={{textAlign: "left", width: "100%"}}>
                                    No categories found
                                </Typography>
                            )}
                        </Grid>
                        </>
                        )}
                    </Grid>
                </Paper>

                {mappingReviewDialogOpen && (
                    <MappingReviewDialog
                        open={mappingReviewDialogOpen}
                        onClose={() => {
                            setMappingReviewDialogOpen(false);
                            setMappingSuggestions([]);
                            setProcessingStep(null);
                        }}
                        mappingSuggestions={mappingSuggestions as any}
                        onApply={handleApplyMappings as any}
                        isProcessing={aiMappingInProgress}
                        processingStep={processingStep}
                        mappingType="categories"
                        isApplying={applyingMappings}
                    />
                )}

                <NavigationButtons
                    allowBack={allowBack}
                    allowNext={allowNext}
                    loading={columnDataLoading}
                    backStep={onBack}
                    nextStep={onNext}
                    activeStep={activeStep}
                    steps={steps}
                    formattedProfile={formattedProfile}
                    profileType={profileType}
                />

                <EnhancedSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                />
            </Grid>
        </>
    );
}
